// Textos de /videografo-madrid por idioma. El ES es copia literal de la página original (no tocar).
// Sin imports de servidor: lo usan page.tsx (metadata) y VideografoMadridClient.tsx.
import type { Lang } from "@/lib/i18n/config"

const es = {
  metaTitle: "Videógrafo en Madrid | Grabación profesional",
  metaDescription:
    "Videógrafo profesional en Madrid para eventos, empresas y contenido corporativo. Vídeo de alta calidad para comunicación de marca.",
  keywords: ["videografo madrid", "grabacion eventos madrid", "video corporativo madrid"],
  title: "Grabación profesional de eventos, empresas y contenido corporativo",
  imageAlt: "Grabación profesional de vídeo para eventos y empresas",
  imageCaption: "Grabación profesional de eventos, empresas y contenido corporativo",
  intro:
    "Si estás buscando un videógrafo profesional en Madrid para tu evento, tu empresa o tu contenido corporativo, puedo ayudarte. Trabajo con empresas e instituciones en la grabación y edición de vídeos que comunican con impacto: eventos, piezas para redes, vídeo corporativo, entrevistas y documentales.",
  servicesTitle: "Servicios como videógrafo en Madrid",
  services: [
    "Video de Eventos Corporativos",
    "Video para redes sociales",
    "Video corporativo",
    "Video formación",
    "Entrevistas",
    "Video animado",
    "Spots Publicitarios",
    "Documentales",
  ],
  whyTitle: "Por qué contratar un videógrafo en Madrid profesional",
  why: [
    "Experiencia y profesionalidad",
    "Equipo técnico de vanguardia",
    "Creatividad y narrativa visual",
    "Optimización para plataformas digitales",
    "Entrega puntual y eficiente",
  ],
  howTitle: "Cómo trabajo",
  how: [
    "Contacto inicial",
    "Propuesta personalizada",
    "Producción y grabación",
    "Edición y postproducción",
    "Entrega final",
  ],
  faqTitle: "Preguntas frecuentes",
  faq: [
    { q: "¿Dónde trabajas?", a: "Estoy basado en Madrid y me desplazo por toda España para rodajes y cobertura de eventos cuando el proyecto lo requiere." },
    { q: "¿Necesito un filmmaker en Madrid para tu evento o proyecto?", a: "No es obligatorio estar en Madrid. Trabajo en Madrid, en otras ciudades y en ubicaciones que tú indiques. Lo importante es encajar en fechas y alcance del proyecto." },
    { q: "Vídeo de eventos corporativos en Madrid", a: "Ofrezco cobertura de eventos corporativos en Madrid y alrededores: congresos, lanzamientos, convenciones, team buildings y experiencias. Incluyo grabación, edición y entregas en los formatos que necesites." },
    { q: "Qué incluyo en la cobertura de tu evento", a: "Grabación en multicámara cuando hace falta, sonido directo, edición del vídeo principal y, si se acuerda, versiones para redes sociales. Todo se detalla en la propuesta según el tipo de evento." },
    { q: "Tipos de eventos que cubro", a: "Congresos y jornadas, lanzamientos de producto, convenciones comerciales, eventos de equipo, presentaciones institucionales, inauguraciones y experiencias de marca." },
    { q: "Por qué un vídeo profesional marca la diferencia?", a: "Un vídeo profesional transmite seriedad y calidad, mejora el recuerdo de tu mensaje y te da material reutilizable para web, redes y campañas. Invertir en buen contenido suele rentabilizarse en imagen y alcance." },
    { q: "Métricas de grabación", a: "Trabajo en 4K cuando el proyecto lo pide y en Full HD como estándar. Formatos y duraciones se acuerdan en la propuesta según el uso final (web, redes, sala, etc.)." },
    { q: "Clientes para los que trabajo", a: "Empresas de todos los tamaños, instituciones públicas, ONGs, agencias, productoras y marcas que necesitan vídeo corporativo, de eventos o para redes. Si tienes un proyecto en mente, cuéntamelo." },
    { q: "Entregables y material", a: "Entrego los vídeos acordados (normalmente MP4 listo para web y redes), versiones cortas si se ha pactado, y el material en bruto solo si se incluye en el presupuesto." },
  ],
}

const en: typeof es = {
  metaTitle: "Videographer in Madrid | Professional Video Filming",
  metaDescription:
    "Professional videographer in Madrid for events, businesses and corporate content. High-quality video production for brand communication.",
  keywords: [
    "videographer madrid",
    "madrid videographer",
    "professional videographer madrid",
    "event filming madrid",
    "corporate video madrid",
  ],
  title: "Professional filming for events, businesses and corporate content",
  imageAlt: "Professional video filming for events and businesses",
  imageCaption: "Professional filming for events, businesses and corporate content",
  intro:
    "If you're looking for a professional videographer in Madrid for your event, your company or your corporate content, I can help. I work with companies and institutions, filming and editing videos that communicate with impact: events, social media content, corporate video, interviews and documentaries.",
  servicesTitle: "Videographer services in Madrid",
  services: [
    "Corporate event video",
    "Social media video",
    "Corporate video",
    "Training video",
    "Interviews",
    "Animated video",
    "Commercials",
    "Documentaries",
  ],
  whyTitle: "Why hire a professional videographer in Madrid",
  why: [
    "Experience and professionalism",
    "State-of-the-art equipment",
    "Creativity and visual storytelling",
    "Optimized for digital platforms",
    "On-time, efficient delivery",
  ],
  howTitle: "How I work",
  how: [
    "Initial contact",
    "Tailored proposal",
    "Production and filming",
    "Editing and post-production",
    "Final delivery",
  ],
  faqTitle: "Frequently asked questions",
  faq: [
    { q: "Where do you work?", a: "I'm based in Madrid and travel all over Spain for shoots and event coverage whenever a project requires it." },
    { q: "Do you need a filmmaker in Madrid for your event or project?", a: "You don't have to be in Madrid. I work in Madrid, in other cities and at whatever locations you choose. What matters is that the project's dates and scope are a good fit." },
    { q: "Corporate event video in Madrid", a: "I cover corporate events in Madrid and the surrounding area: conferences, launches, conventions, team-building days and brand experiences. Filming, editing and delivery in whatever formats you need are all included." },
    { q: "What's included in your event coverage", a: "Multi-camera filming when needed, on-site sound recording, editing of the main video and, if agreed, versions for social media. Everything is detailed in the proposal based on the type of event." },
    { q: "Types of events I cover", a: "Conferences and summits, product launches, sales conventions, team events, institutional presentations, openings and brand experiences." },
    { q: "Why does professional video make a difference?", a: "Professional video conveys credibility and quality, makes your message more memorable and gives you reusable material for your website, social media and campaigns. Investing in good content usually pays off in brand image and reach." },
    { q: "Filming specs", a: "I shoot in 4K when the project calls for it and in Full HD as standard. Formats and running times are agreed in the proposal based on the final use (web, social media, big screen, etc.)." },
    { q: "Who I work with", a: "Companies of all sizes, public institutions, NGOs, agencies, production companies and brands that need corporate video, event video or social media content. If you have a project in mind, tell me about it." },
    { q: "Deliverables and footage", a: "I deliver the agreed videos (usually MP4 files ready for web and social media), short versions if agreed, and raw footage only if it's included in the quote." },
  ],
}

export const content: Record<Lang, typeof es> = { es, en }
